import { SESSION_ENDED_MESSAGE, readLoginContext } from '@/providers/session-end';
import { useState } from 'react';
import { useLogin } from '@refinedev/core';
import { QRCodeSVG } from 'qrcode.react';
import {
  completeNewPassword,
  completeMfaSetup,
  submitMfaCode,
  getMfaSetup,
} from '../../providers/auth-provider';

// Mirrors the admin pool password policy: >=14 chars with an uppercase,
// lowercase, number, and symbol (stricter than the consumer pool).
const PASSWORD_HINT =
  'At least 14 characters, with an uppercase, lowercase, number, and symbol.';

function validateNewPassword(pw: string, confirm: string): string | null {
  if (!pw) return 'Please enter a new password.';
  if (pw.length < 14) return 'Password must be at least 14 characters.';
  if (!/[A-Z]/.test(pw)) return 'Password must contain an uppercase letter.';
  if (!/[a-z]/.test(pw)) return 'Password must contain a lowercase letter.';
  if (!/[0-9]/.test(pw)) return 'Password must contain a number.';
  if (!/[^A-Za-z0-9]/.test(pw)) return 'Password must contain a symbol.';
  if (pw !== confirm) return 'Passwords do not match.';
  return null;
}

type Step = 'credentials' | 'newPassword' | 'mfaSetup' | 'mfaCode';
type ChallengeError = { name?: string; message?: string } | undefined;

const inputClass =
  'w-full rounded-md border bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring';
const buttonClass =
  'w-full rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50';
const onlyDigits = (s: string) => s.replace(/\D/g, '').slice(0, 6);

/**
 * Admin sign-in with mandatory TOTP MFA (admin-identity-plane Phase 1). The
 * dedicated admin pool enforces MFA, so the flow is a short challenge chain:
 *   - new admin:       password → set new password → enroll TOTP (scan QR) → in
 *   - returning admin: password → TOTP code → in
 * The auth-provider resolves each challenge with a named error this component
 * maps to the next step; on success the Cognito session is established and we
 * hard-navigate to /dashboard so the app re-bootstraps authenticated.
 */
export function LoginPage() {
  const { mutate: login, isLoading } = useLogin();

  const [step, setStep] = useState<Step>('credentials');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState<string | null>(null);

  // New-password challenge (FORCE_CHANGE_PASSWORD).
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // MFA.
  const [mfa, setMfa] = useState<{ secret: string; otpauthUri: string } | null>(null);
  const [code, setCode] = useState('');

  const [stepError, setStepError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Why this page is showing, and where to go back to (session-end.ts).
  const loginContext = readLoginContext(window.location.search);
  const goToDashboard = () => window.location.assign(loginContext.returnTo ?? '/dashboard');

  /** Map a named challenge error to the next step. Returns true if it advanced. */
  const advanceFromChallenge = (err: ChallengeError): boolean => {
    switch (err?.name) {
      case 'NewPasswordRequired':
        setStep('newPassword');
        setStepError(null);
        return true;
      case 'MfaSetupRequired':
        setMfa(getMfaSetup());
        setCode('');
        setStep('mfaSetup');
        setStepError(null);
        return true;
      case 'SoftwareTokenMfaRequired':
        setCode('');
        setStep('mfaCode');
        setStepError(null);
        return true;
      default:
        return false;
    }
  };

  const handleCredentials = (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError(null);
    login(
      { email, password },
      {
        onSuccess: (result) => {
          if (result?.success) {
            goToDashboard();
            return;
          }
          const err = result?.error as ChallengeError;
          if (advanceFromChallenge(err)) return;
          if (err?.message) setLoginError(err.message);
        },
        onError: (error) => setLoginError(error?.message || 'Invalid credentials'),
      },
    );
  };

  // Generic submit wrapper for the post-credentials steps (they return a
  // LoginResult and may chain to a further challenge).
  const runStep = async (fn: () => Promise<{ success: boolean; error?: { name: string; message: string } }>) => {
    setStepError(null);
    setSubmitting(true);
    try {
      const result = await fn();
      if (result.success) {
        goToDashboard();
        return;
      }
      if (result.error?.name === 'NoChallenge') {
        // Lost the challenge (page reload, timeout) — restart.
        setStep('credentials');
        setNewPassword('');
        setConfirmPassword('');
        setCode('');
        setLoginError(result.error.message);
        return;
      }
      if (advanceFromChallenge(result.error)) return;
      setStepError(result.error?.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleNewPassword = (e: React.FormEvent) => {
    e.preventDefault();
    const v = validateNewPassword(newPassword, confirmPassword);
    if (v) {
      setStepError(v);
      return;
    }
    runStep(() => completeNewPassword(newPassword));
  };
  const handleMfaSetup = (e: React.FormEvent) => {
    e.preventDefault();
    runStep(() => completeMfaSetup(code));
  };
  const handleMfaCode = (e: React.FormEvent) => {
    e.preventDefault();
    runStep(() => submitMfaCode(code));
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <div className="w-full max-w-md space-y-6 rounded-lg border bg-card p-8 shadow-lg">
        <div className="text-center">
          <h1 className="text-2xl font-bold">
            {step === 'newPassword' ? 'Set your new password' : 'UDC Admin Panel'}
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {step === 'credentials' && 'Sign in to access the admin dashboard'}
            {step === 'newPassword' && 'Your temporary password worked — choose a new password to continue.'}
            {step === 'mfaSetup' && 'Set up two-factor authentication (required).'}
            {step === 'mfaCode' && 'Enter the code from your authenticator app.'}
          </p>
          {loginContext.sessionEnded && step === 'credentials' && (
            <p role="status" className="mt-3 rounded-md border bg-muted/50 p-2 text-sm">
              {SESSION_ENDED_MESSAGE}
            </p>
          )}
        </div>

        {stepError && (
          <div role="alert" className="rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm font-medium text-destructive">
            {stepError}
          </div>
        )}

        {step === 'credentials' && (
          <form onSubmit={handleCredentials} className="space-y-6">
            <div className="space-y-2">
              <label htmlFor="email" className="text-sm font-medium">Email</label>
              <input id="email" type="email" autoComplete="username" value={email}
                onChange={(e) => setEmail(e.target.value)} className={inputClass}
                placeholder="admin@uniquedatingcompany.com" required />
            </div>
            <div className="space-y-2">
              <label htmlFor="password" className="text-sm font-medium">Password</label>
              <input id="password" type="password" autoComplete="current-password" value={password}
                onChange={(e) => setPassword(e.target.value)} className={inputClass}
                placeholder="Enter your password" required />
            </div>
            {loginError && <p className="text-sm font-medium text-destructive">{loginError}</p>}
            <button type="submit" disabled={isLoading} className={buttonClass}>
              {isLoading ? 'Signing in…' : 'Sign In'}
            </button>
          </form>
        )}

        {step === 'newPassword' && (
          <form onSubmit={handleNewPassword} className="space-y-6">
            <div className="space-y-2">
              <label htmlFor="new-password" className="text-sm font-medium">New password</label>
              <input id="new-password" type="password" autoComplete="new-password" value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)} className={inputClass}
                placeholder="Enter a new password" required />
            </div>
            <div className="space-y-2">
              <label htmlFor="confirm-password" className="text-sm font-medium">Confirm new password</label>
              <input id="confirm-password" type="password" autoComplete="new-password" value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)} className={inputClass}
                placeholder="Re-enter the new password" required />
            </div>
            <p className="text-xs text-muted-foreground">{PASSWORD_HINT}</p>
            <button type="submit" disabled={submitting} className={buttonClass}>
              {submitting ? 'Setting password…' : 'Set password & continue'}
            </button>
          </form>
        )}

        {step === 'mfaSetup' && (
          <form onSubmit={handleMfaSetup} className="space-y-5">
            <p className="text-sm text-muted-foreground">
              Scan this QR with an authenticator app (Google Authenticator, 1Password, Authy),
              then enter the 6-digit code it shows.
            </p>
            {mfa && (
              <div className="flex flex-col items-center space-y-3">
                <div className="rounded-md bg-white p-3">
                  <QRCodeSVG value={mfa.otpauthUri} size={176} />
                </div>
                <details className="w-full text-xs text-muted-foreground">
                  <summary className="cursor-pointer">Can&apos;t scan? Enter this key manually</summary>
                  <code className="mt-1 block break-all rounded bg-muted px-2 py-1 font-mono">{mfa.secret}</code>
                </details>
              </div>
            )}
            <div className="space-y-2">
              <label htmlFor="setupCode" className="text-sm font-medium">Authenticator code</label>
              <input id="setupCode" inputMode="numeric" autoComplete="one-time-code" value={code}
                onChange={(e) => setCode(onlyDigits(e.target.value))} className={inputClass}
                placeholder="123456" required />
            </div>
            <button type="submit" disabled={submitting || code.length < 6} className={buttonClass}>
              {submitting ? 'Verifying…' : 'Verify & finish setup'}
            </button>
          </form>
        )}

        {step === 'mfaCode' && (
          <form onSubmit={handleMfaCode} className="space-y-5">
            <div className="space-y-2">
              <label htmlFor="mfaCode" className="text-sm font-medium">Authenticator code</label>
              <input id="mfaCode" inputMode="numeric" autoComplete="one-time-code" autoFocus value={code}
                onChange={(e) => setCode(onlyDigits(e.target.value))} className={inputClass}
                placeholder="123456" required />
            </div>
            <button type="submit" disabled={submitting || code.length < 6} className={buttonClass}>
              {submitting ? 'Verifying…' : 'Verify'}
            </button>
          </form>
        )}

        <p className="text-center text-xs text-muted-foreground">
          Access restricted to authorized administrators only.
        </p>
      </div>
    </div>
  );
}
